/**
 * @file Корневой компонент приложения мембраны
 * @module membrane/src/App
 * 
 * Управляет состоянием слоёв мембраны и переходами между ними.
 * Реализует принцип "мало экранов - много состояний".
 */

import { useState, useEffect, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import DeviceContainer from './components/DeviceContainer';
import Layer0Field from './layers/Layer0Field';
import RevealMapScene from './layers/RevealMapScene';
import Layer0Sphere from './layers/Layer0Sphere';
import Layer0Flow from './layers/Layer0Flow';
import Layer1Surface from './layers/Layer1Surface';
import Layer2Image from './layers/Layer2Image';
import Layer3Hint from './layers/Layer3Hint';
import Layer4Path from './layers/Layer4Path';
import Layer5ContactForm from './layers/Layer5ContactForm';
import Layer6Protected from './layers/Layer6Protected';
import { LayerState } from './types/membrane';

/**
 * Компонент App - корневой контейнер мембраны
 * 
 * Управляет активными слоями и переходами между ними.
 * Каждый слой может быть активирован независимо или последовательно.
 */
type Layer0State = 'field' | 'crystallization' | 'sphere' | 'flow' | null;

function App() {
  // Слой 0 - живое поле (активен по умолчанию)
  const [layer0State, setLayer0State] = useState<Layer0State>('field');
  const [clickPosition, setClickPosition] = useState<{ x: number; y: number } | null>(null);
  
  // Остальные слои
  const [activeLayers, setActiveLayers] = useState<Set<number>>(new Set());
  const [layerStates, setLayerStates] = useState<Record<number, LayerState>>({});

  /**
   * Активирует слой мембраны
   * @param layerNumber - номер слоя (1-6)
   * @param depth - глубина взаимодействия (0-1)
   */
  const activateLayer = useCallback((layerNumber: number, depth: number = 0) => {
    console.log('activateLayer called:', layerNumber, depth);
    setActiveLayers((prev) => {
      const next = new Set([...prev, layerNumber]);
      console.log('New activeLayers:', Array.from(next));
      return next;
    });
    setLayerStates((prev) => ({
      ...prev,
      [layerNumber]: { active: true, depth },
    }));
  }, []);

  /**
   * Деактивирует слой (позволяет вернуться назад)
   * @param layerNumber - номер слоя
   */
  const deactivateLayer = (layerNumber: number) => {
    setActiveLayers((prev) => {
      const next = new Set(prev);
      next.delete(layerNumber);
      return next;
    });
  };

  /**
   * Обработка первого клика в поле - кристаллизация
   */
  useEffect(() => {
    const handleMembraneClick = (e: Event) => {
      const customEvent = e as CustomEvent<{ x: number; y: number }>;
      setClickPosition(customEvent.detail);
      setLayer0State('crystallization');
    };

    window.addEventListener('membrane-click', handleMembraneClick as EventListener);
    return () => window.removeEventListener('membrane-click', handleMembraneClick as EventListener);
  }, []);

  const [selectedSphereId, setSelectedSphereId] = useState<string>('1');
  const [selectedLinkId, setSelectedLinkId] = useState<string>('1');

  /**
   * Переход в сферу
   */
  const handleEnterSphere = (sphereId?: string) => {
    if (sphereId) {
      setSelectedSphereId(sphereId);
    }
    setLayer0State('sphere');
  };

  // Обработка события входа в сферу
  useEffect(() => {
    const handleSphereEnter = (e: Event) => {
      const customEvent = e as CustomEvent<{ sphereId: string }>;
      setSelectedSphereId(customEvent.detail.sphereId);
    };

    window.addEventListener('membrane-sphere-enter', handleSphereEnter as EventListener);
    return () => window.removeEventListener('membrane-sphere-enter', handleSphereEnter as EventListener);
  }, []);

  /**
   * Переход в поток
   */
  const handleEnterFlow = (linkId?: string) => {
    if (linkId) {
      setSelectedLinkId(linkId);
    }
    setLayer0State('flow');
  };

  // Обработка события входа в поток
  useEffect(() => {
    const handleLinkEnter = (e: Event) => {
      const customEvent = e as CustomEvent<{ linkId: string }>;
      setSelectedLinkId(customEvent.detail.linkId);
    };

    window.addEventListener('membrane-link-enter', handleLinkEnter as EventListener);
    return () => window.removeEventListener('membrane-link-enter', handleLinkEnter as EventListener);
  }, []);

  /**
   * Возврат на предыдущий уровень (Esc)
   * Теперь Layer0Sphere сам обрабатывает Esc, поэтому здесь только для других слоёв
   */
  const handleEscape = () => {
    // Layer0Sphere обрабатывает Esc сам, пропускаем его
    if (layer0State === 'sphere' || layer0State === 'flow') {
      // Layer0Sphere сам обработает Esc через свой onReturn
      return;
    }
    if (layer0State === 'crystallization') {
      setLayer0State('field');
    } else if (activeLayers.size > 0) {
      // Возврат из обычных слоёв
      const layersArray = Array.from(activeLayers).sort((a, b) => b - a);
      if (layersArray.length > 0) {
        deactivateLayer(layersArray[0]);
      }
    }
  };

  // Обработка клавиши Esc и Break
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Pause' || e.code === 'Pause') {
        handleEscape();
      }
      // Временная активация Layer1 для тестирования (Ctrl+Shift+U)
      if (e.ctrlKey && e.shiftKey && (e.key === 'U' || e.key === 'u' || e.keyCode === 85)) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        console.log('Ctrl+Shift+U pressed, activating Layer1Surface...');
        activateLayer(1, 0.1);
        // Используем setTimeout для чтения актуального состояния после обновления
        setTimeout(() => {
          setActiveLayers((current) => {
            console.log('Layer1Surface activated, activeLayers:', Array.from(current));
            return current;
          });
        }, 0);
      }
    };

    // Добавляем обработчики на window и document в capture phase для надежности
    window.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [layer0State, activeLayers, activateLayer]);

  return (
    <DeviceContainer>
      <div className="relative w-full h-full min-h-screen overflow-hidden" style={{ width: '100%', height: '100%' }}>
        <AnimatePresence>
        {/* Слой 0 - Живое поле созерцания */}
        {layer0State === 'field' && (
          <Layer0Field
            key="layer0-field"
            onProceed={() => {
              // Координаты будут переданы через событие
            }}
            state={{ active: true, depth: 0 }}
          />
        )}

        {/* Слой 0 - Кристаллизация структуры */}
        {layer0State === 'crystallization' && (
          <RevealMapScene
            key="layer0-crystallization"
            onProceed={handleEnterSphere}
            onEnterFlow={handleEnterFlow}
            onReturn={() => setLayer0State('field')}
            state={{ active: true, depth: 0.1 }}
            clickPosition={clickPosition || undefined}
          />
        )}

        {/* Слой 0 - Внутреннее пространство сферы */}
        {layer0State === 'sphere' && (
          <Layer0Sphere
            key="layer0-sphere"
            onReturn={() => setLayer0State('crystallization')}
            state={{ active: true, depth: 0.3 }}
            sphereId={selectedSphereId}
          />
        )}

        {/* Слой 0 - Потоки связей */}
        {layer0State === 'flow' && (
          <Layer0Flow
            key="layer0-flow"
            onProceed={() => handleEnterSphere()}
            onReturn={() => setLayer0State('crystallization')}
            state={{ active: true, depth: 0.2 }}
            linkId={selectedLinkId}
          />
        )}

        {/* Слой 1 - Лёгкая поверхность */}
        {activeLayers.has(1) && (
          <Layer1Surface
            key="layer1"
            onProceed={() => activateLayer(2, 0.2)}
            state={layerStates[1]}
          />
        )}

        {/* Слой 2 - Образ Начала */}
        {activeLayers.has(2) && (
          <Layer2Image
            key="layer2"
            onProceed={() => activateLayer(3, 0.4)}
            onReturn={() => deactivateLayer(2)}
            state={layerStates[2]}
          />
        )}

        {/* Слой 3 - Намёк на глубину */}
        {activeLayers.has(3) && (
          <Layer3Hint
            key="layer3"
            onProceed={() => activateLayer(4, 0.6)}
            onReturn={() => deactivateLayer(3)}
            state={layerStates[3]}
          />
        )}

        {/* Слой 4 - Путь углубления */}
        {activeLayers.has(4) && (
          <Layer4Path
            key="layer4"
            onProceed={() => activateLayer(5, 0.8)}
            onReturn={() => deactivateLayer(4)}
            state={layerStates[4]}
          />
        )}

        {/* Слой 5 - Встреча ширнормассы */}
        {activeLayers.has(5) && (
          <Layer5ContactForm
            key="layer5"
            onProceed={() => activateLayer(6, 1.0)}
            onReturn={() => deactivateLayer(5)}
            state={layerStates[5]}
          />
        )}

        {/* Слой 6 - Глубинное пространство (защищённый) */}
        {activeLayers.has(6) && (
          <Layer6Protected
            key="layer6"
            onReturn={() => deactivateLayer(6)}
            state={layerStates[6]}
          />
        )}
        </AnimatePresence>
      </div>
    </DeviceContainer>
  );
}

export default App;

